import {
  createInertiaApp,
  router,
  type ResolvedComponent,
} from "@inertiajs/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import type { FlashData } from "@/types";
import "./i18n";

let previousFlash: FlashData = {};

window.addEventListener("popstate", (event) => {
  event.stopImmediatePropagation();

  router.visit(window.location.href, {
    preserveState: false,
    preserveScroll: false,
    replace: true,
  });
});

router.on("success", (event) => {
  const page = event.detail.page;

  const flash = (page.props.flash ?? {}) as FlashData;
  if (flash.alert && flash.alert !== previousFlash.alert) {
    toast.error(flash.alert);
  }
  if (flash.notice && flash.notice !== previousFlash.notice) {
    toast.success(flash.notice);
  }
  previousFlash = flash;
});

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/**/*.tsx"
    );
    const importer = pages[`../pages/${name}.tsx`];
    if (!importer) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    return importer();
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
        <Toaster position="top-center" />
      </StrictMode>
    );
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.'
    );
  }
});
